import React, { useEffect, useRef, useState } from 'react';
import './Hero.css';

const STATS = [
  { value: '10+',  label: "Ans d'expérience" },
  { value: '500+', label: 'Installations' },
  { value: '98%',  label: 'Clients satisfaits' },
  { value: '24h',  label: "Intervention" },
];

const FEATURES = [
  { label: 'RGE QualiPAC certifié' },
  { label: 'Devis gratuit sous 24h' },
  { label: 'Intervention 24h/24' },
  { label: 'Île-de-France' },
];

export default function Hero() {
  const [mounted, setMounted] = useState(false);
  const canvasRef = useRef(null);

  useEffect(() => {
    const t = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(t);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const particles = Array.from({ length: 40 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      r: Math.random() * 1.2 + 0.2,
      dx: (Math.random() - 0.5) * 0.18,
      dy: -(Math.random() * 0.28 + 0.06),
      alpha: Math.random() * 0.28 + 0.06,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${p.alpha})`;
        ctx.fill();
        p.x += p.dx; p.y += p.dy;
        if (p.y < -4) { p.y = canvas.height + 4; p.x = Math.random() * canvas.width; }
        if (p.x < -4) p.x = canvas.width + 4;
        if (p.x > canvas.width + 4) p.x = -4;
      });
      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => { cancelAnimationFrame(animId); ro.disconnect(); };
  }, []);

  return (
    <section id="accueil" className="hero">

      {/* ── Background layers ── */}
      <div className="hero__bg" aria-hidden="true">
        <div className="hero__bg-base" />
        <div className="hero__bg-grid" />
        <div className="hero__bg-glow-r" />
        <div className="hero__bg-glow-b" />
        <canvas ref={canvasRef} className="hero__canvas" />
      </div>

      {/* ── Main content ── */}
      <div className={`hero__wrap container${mounted ? ' hero__wrap--in' : ''}`}>

        {/* ─ Left column ─ */}
        <div className="hero__content">

          {/* Eyebrow */}
          <div className="hero__eyebrow">
            <span className="hero__eyebrow-dot" />
            <span>Certifié RGE QualiPAC · Île-de-France</span>
          </div>

          {/* Headline */}
          <h1 className="hero__h1">
            <span className="hero__h1-line hero__h1-line--a">Votre confort</span>
            <span className="hero__h1-line hero__h1-line--b">thermique</span>
            <span className="hero__h1-line hero__h1-line--c">toute l'année</span>
          </h1>

          {/* Accent rule */}
          <div className="hero__accent" aria-hidden="true">
            <span className="hero__accent-bar" />
            <span className="hero__accent-dot" />
          </div>

          {/* Description */}
          <p className="hero__lead">
            Spécialiste en climatisation, pompe à chaleur et ventilation.
            Installation, entretien et dépannage pour particuliers et professionnels.
          </p>

          {/* Features */}
          <ul className="hero__feats" aria-label="Points forts">
            {FEATURES.map((f, i) => (
              <li key={i} className="hero__feat">
                <CheckIcon />
                {f.label}
              </li>
            ))}
          </ul>

          {/* CTAs */}
          <div className="hero__actions">
            <a href="#contact" className="hero__btn-cta">
              <PhoneIcon />
              Devis gratuit
              <span className="hero__btn-arrow" aria-hidden="true">→</span>
            </a>
            <a href="#services" className="hero__btn-outline">
              Nos services
              <ArrowIcon />
            </a>
          </div>

          {/* Stats row */}
          <div className="hero__stats">
            {STATS.map((s, i) => (
              <React.Fragment key={i}>
                <div className="hero__stat">
                  <span className="hero__stat-val">{s.value}</span>
                  <span className="hero__stat-lbl">{s.label}</span>
                </div>
                {i < STATS.length - 1 && <div className="hero__stat-sep" aria-hidden="true" />}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* ─ Right column — visual card ─ */}
        <div className="hero__visual">
          <div className="hero__visual-orb" aria-hidden="true" />

          {/* Badge cert */}
          <div className="hero__badge hero__badge--tl">
            <ShieldIcon />
            <div>
              <strong>Certifié</strong>
              <span>QualiPAC 2024</span>
            </div>
          </div>

          <ClimaCard />

          {/* Badge rapidity */}
          <div className="hero__badge hero__badge--br">
            <ClockIcon />
            <div>
              <strong>24h/24</strong>
              <span>Intervention rapide</span>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}

/* ─────────────────────────────────────────
   Card
───────────────────────────────────────── */
function ClimaCard() {
  const bars = [55, 72, 48, 88, 65, 80, 58];
  return (
    <div className="hcard">
      <div className="hcard__shine" aria-hidden="true" />

      <div className="hcard__head">
        <div className="hcard__icon">
          <AcIcon />
        </div>
        <div className="hcard__info">
          <span className="hcard__name">SOCLIMATE</span>
          <span className="hcard__sub">Système climatisation</span>
        </div>
        <span className="hcard__badge">RGE</span>
      </div>

      <div className="hcard__body">
        <div className="hcard__temp">
          <span className="hcard__deg">22</span>
          <span className="hcard__unit">°C</span>
        </div>
        <span className="hcard__mode">Confort optimal</span>
        <div className="hcard__bars" aria-hidden="true">
          {bars.map((h, i) => (
            <div key={i} className="hcard__bar"
              style={{ '--h': h + '%', '--i': i }} />
          ))}
        </div>
      </div>

      <div className="hcard__foot">
        <span className="hcard__live">
          <span className="hcard__live-dot" />
          Système actif
        </span>
        <span className="hcard__eff">A+++</span>
      </div>
    </div>
  );
}

/* ─── Inline SVG icons ─── */
const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <circle cx="7" cy="7" r="7" fill="rgba(200,16,46,0.15)" />
    <path d="M4 7l2 2 4-4" stroke="#e30010" strokeWidth="1.6"
      strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PhoneIcon = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.89 9.11 19.79 19.79 0 01.82.5 2 2 0 012.81 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L7.09 7.91a16 16 0 006 6l.91-1a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 14.92z" />
  </svg>
);

const ArrowIcon = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

const ShieldIcon = () => (
  <svg width="18" height="18" fill="none" stroke="#4ade80" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

const ClockIcon = () => (
  <svg width="18" height="18" fill="none" stroke="#fbbf24" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v6l4 2" strokeLinecap="round" />
  </svg>
);

const AcIcon = () => (
  <svg width="24" height="24" fill="none" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M3 14h18M3 18h12" stroke="white" strokeWidth="1.7" strokeLinecap="round" />
    <path d="M5 14V9a2 2 0 012-2h10a2 2 0 012 2v5" stroke="white" strokeWidth="1.7" strokeLinecap="round" />
    <path d="M9 9V7M12 9V6M15 9V7" stroke="#93c5fd" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);